import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Trash2, X, Tag, ChevronDown, Rocket } from "lucide-react";
import { STATUS_OPTIONS, type ArticleStatus } from "./StatusPicker";
import { Badge } from "@/components/ui/badge";

interface ArticleBulkBarProps {
  count: number;
  onDelete: () => void;
  onClear: () => void;
  isDeleting: boolean;
  onChangeStatus?: (status: ArticleStatus) => void;
  isUpdatingStatus?: boolean;
  onPublish?: () => void;
  isPublishing?: boolean;
}

export default function ArticleBulkBar({
  count, onDelete, onClear, isDeleting, onChangeStatus, isUpdatingStatus, onPublish, isPublishing,
}: ArticleBulkBarProps) {
  const { t } = useTranslation();
  if (count === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-3 shadow-2xl">
      <span className="text-sm font-medium text-foreground">
        {count} article{count > 1 ? "s" : ""} selected
      </span>

      {onChangeStatus && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={isUpdatingStatus}>
              <Tag className="h-4 w-4 mr-1" /> Change status <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-48">
            <DropdownMenuLabel className="text-xs">Move selected to…</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {STATUS_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => onChangeStatus(opt.value)}
                className="text-xs gap-2 cursor-pointer"
              >
                <Badge className={`${opt.classes} text-[10px] gap-1 font-medium border-transparent`}>
                  {opt.icon}
                  {opt.label}
                </Badge>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {onPublish && (
        <Button size="sm" onClick={onPublish} disabled={isPublishing}>
          <Rocket className="h-4 w-4 mr-1" /> Publish
        </Button>
      )}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm" disabled={isDeleting}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to delete {count} article{count > 1 ? "s" : ""}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <IconButton variant="ghost" className="h-7 w-7" onClick={onClear} label={t("actions.deselectAll")}>
        <X className="h-4 w-4" />
      </IconButton>
    </div>
  );
}
